import config from 'shared/config'

export const urlForConversation = (conversationId: number | string) =>
  `/conversations/${conversationId}`

export const deepLinkUrlForConversation = (conversationId: number) =>
  `${config.BASE_URL}/conversations/deep-link?url=${encodeURIComponent(
    `conversations/${conversationId}`
  )}`

export const urlForVisitor = (endUserId: string | number) => {
  if (endUserId === '') {
    return '/conversations'
  }
  return `/conversations/visitors/${endUserId}`
}

export const prefixUrlWithHttp = (link?: string): string | undefined => {
  if (!link) return link

  const VALID_PREFIXES = ['http://', 'https://', 'mailto:']

  for (const prefix of VALID_PREFIXES) {
    if (link.startsWith(prefix)) {
      return link
    }
  }

  return `http://${link}`
}

const escapeUrlComponent = (str: string | null) => {
  if (typeof str !== 'string') return ''
  // Target characters that could be dangerous (like angle brackets, quotes)
  return str.replace(/[<>'"\\]/g, (match) => {
    // Replace with simple percent-encoded values
    switch (match) {
      case '<':
        return '%3C'
      case '>':
        return '%3E'
      case '"':
        return '%22'
      case "'":
        return '%27'
      case '\\':
        return '%5C'
      default:
        return match
    }
  })
}

export const sanitizeUrl = (unsafeUrl: string | null) => {
  // Define allowed protocols internally for a zero-parameter dependency
  const ALLOWED_PROTOCOLS = ['http:', 'https:']

  if (typeof unsafeUrl !== 'string' || unsafeUrl.trim().length === 0) {
    return null
  }

  try {
    const url = new URL(unsafeUrl.trim())
    const protocol = url.protocol.toLowerCase()

    // 1. Protocol Validation: Strictly enforce HTTP/HTTPS
    if (!ALLOWED_PROTOCOLS.includes(protocol)) {
      // Rejects schemes like 'javascript:', 'data:', 'file:', 'ftp:'
      return null
    }

    // 2. Query Parameter Cleaning
    const safeSearchParams = new URLSearchParams()
    for (const [key, value] of url.searchParams.entries()) {
      // Clean both key and value to prevent injection attempts
      const safeKey = escapeUrlComponent(key)
      const safeValue = escapeUrlComponent(value)
      safeSearchParams.append(safeKey, safeValue)
    }

    // 3. Path and Host Cleaning
    // Clean the pathname using the helper function.
    const safePathname = escapeUrlComponent(url.pathname)

    // 4. Reconstruct the fully safe URL
    let safeUrl = `${protocol}//${url.host}${safePathname}`

    if (safeSearchParams.toString().length > 0) {
      safeUrl += `?${safeSearchParams.toString()}`
    }

    // Append the hash (fragment) if present
    if (url.hash) {
      // Minimal cleaning for the hash fragment
      safeUrl += url.hash
    }

    return safeUrl
  } catch (e) {
    // Catches TypeError thrown by new URL() for malformed URLs
    return null
  }
}
